<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-backdrop" @click="$emit('close')">
      <div class="modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="header-left">
              <font-awesome-icon icon="fa-solid fa-pen-to-square" class="header-icon"/>
              <div>
                <h5>Adjust Order</h5>
                <p class="order-number-text">{{ order?.orderNumber }}</p>
              </div>
            </div>
            <button class="btn-close" @click="$emit('close')">
              <font-awesome-icon icon="fa-solid fa-xmark"/>
            </button>
          </div>
          <div class="gradient-accent"></div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="!adjustments" class="loading-state">
            <div class="spinner"></div>
            <p>Loading items...</p>
          </div>
          <div v-else class="adjust-form">
            <div v-for="(item, index) in adjustments" :key="item.itemId" class="adjust-item">
              <div class="item-header">
                <div class="item-name">{{ item.productName }}</div>
                <div class="item-qty-badge">Current: {{ item.originalQuantity }}</div>
              </div>
              <div class="item-controls">
                <select v-model="item.action" class="form-select" @change="onActionChange(item)">
                  <option value="ACCEPT">Accept</option>
                  <option value="ADJUST_QUANTITY">Adjust Quantity</option>
                  <option value="REJECT">Remove Item</option>
                </select>
                <input
                  v-if="item.action === 'ADJUST_QUANTITY'"
                  v-model.number="item.newQuantity"
                  @input="onQuantityInput(item)"
                  @blur="onQuantityInput(item)"
                  type="number"
                  class="form-control"
                  step="0.5"
                  min="0"
                  :max="item.originalQuantity"
                  placeholder="New quantity"
                >
                <input
                  v-model.trim="item.itemReason"
                  type="text"
                  class="form-control item-reason"
                  :placeholder="item.action === 'ACCEPT' ? 'Item-specific reason (optional)' : 'Item-specific reason (required)'"
                >
              </div>
              <div class="predefined-reasons">
                <button
                  v-for="reason in predefinedItemReasons"
                  :key="reason"
                  class="btn-reason"
                  @click.prevent="applyItemReason(item, reason)"
                >
                  {{ reason }}
                </button>
              </div>
            </div>
            <hr class="divider">
            <div class="form-group">
              <label class="form-label">
                <font-awesome-icon icon="fa-solid fa-comment-dots" class="label-icon"/>
                Overall Reason for Adjustments
              </label>
              <textarea
                v-model.trim="overallReason"
                class="form-control"
                placeholder="Required: explain the order changes"
                rows="3"
              ></textarea>
              <div class="predefined-reasons">
                <button
                  v-for="reason in predefinedOverallReasons"
                  :key="reason"
                  class="btn-reason"
                  @click.prevent="applyOverallReason(reason)"
                >
                  {{ reason }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn-secondary" @click="$emit('close')">
            <font-awesome-icon icon="fa-solid fa-times"/>
            Cancel
          </button>
          <button class="btn-primary" @click="submit" :disabled="!isFormValid">
            <font-awesome-icon icon="fa-solid fa-check"/>
            Submit Adjustments
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Swal from 'sweetalert2';
import { Storage } from '@/services/Storage';
import storeOrdersService from '@/services/storeOrdersService';

export default {
  name: 'AdjustOrderModal',
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null }
  },
  data() {
    return {
      adjustments: null,
      overallReason: '',
      isLoadingOrder: false,
      loadRequestId: 0,
      predefinedItemReasons: [
        'Item out of stock',
        'Partial quantity available',
        'Customer requested change',
      ],
      predefinedOverallReasons: [
        'Some items unavailable',
        'Customer updated order',
        'Store preparing order',
      ]
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (!visible) {
          this.resetState();
          return;
        }
        this.loadOrderItems();
      }
    },
    order: {
      immediate: true,
      handler(newOrder) {
        if (!this.show) return;
        if (newOrder?.id) return this.loadOrderItems();
        if (newOrder?.items) this.initializeAdjustments(newOrder.items);
      }
    }
  },
  computed: {
    isFormValid() {
      if (!Array.isArray(this.adjustments) || this.adjustments.length === 0) return false;

      const hasChanges = this.adjustments.some(item => (
        item.action === 'REJECT' ||
        (item.action === 'ADJUST_QUANTITY' && Number(item.newQuantity) !== Number(item.originalQuantity))
      ));
      if (!hasChanges) return false;
      if (!String(this.overallReason || '').trim()) return false;

      return this.adjustments.every(item => {
        if (item.action === 'ADJUST_QUANTITY') {
          const qty = Number(item.newQuantity);
          const originalQty = Number(item.originalQuantity);
          return (
            Number.isFinite(qty) &&
            qty >= 0 &&
            qty <= originalQty &&
            qty !== originalQty &&
            String(item.itemReason || '').trim().length > 0
          );
        }
        if (item.action === 'REJECT') {
          return String(item.itemReason || '').trim().length > 0;
        }
        return true;
      });
    }
  },
  methods: {
    resetState() {
      this.adjustments = null;
      this.overallReason = '';
      this.isLoadingOrder = false;
    },
    async loadOrderItems() {
      const requestId = ++this.loadRequestId;
      this.adjustments = null;
      this.isLoadingOrder = true;
      this.overallReason = '';

      try {
        let items = this.order?.items || [];

        if (this.order?.id) {
          const store = await Storage.getDefaultStore();
          const storeDB = store?.StoreDB || store?.storeDB || store?.db || store?.slug;
          if (!storeDB) throw new Error('Default store not set');

          const response = await storeOrdersService.getOrder(storeDB, this.order.id);
          const orderData = response?.data || response?.order || null;
          if (orderData && Array.isArray(orderData.items)) {
            items = orderData.items;
          }
        }

        if (requestId !== this.loadRequestId) return;
        this.initializeAdjustments(items);
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.initializeAdjustments(this.order?.items || []);
        await Swal.fire({
          icon: 'warning',
          title: 'Using Cached Items',
          text: 'Could not refresh order items from server. Please verify quantities carefully.',
        });
      } finally {
        if (requestId === this.loadRequestId) {
          this.isLoadingOrder = false;
        }
      }
    },
    initializeAdjustments(items) {
      this.adjustments = items.map(item => ({
        itemId: item.id || item.productPlu || Math.random().toString(36).substr(2, 9),
        productName: item.productName || item.description,
        originalQuantity: Number(item.quantity ?? item.Qty ?? 0),
        action: 'ACCEPT',
        newQuantity: Number(item.quantity ?? item.Qty ?? 0),
        itemReason: ''
      }));
    },
    onActionChange(item) {
      if (item.action !== 'ADJUST_QUANTITY') {
        item.newQuantity = item.originalQuantity;
      }
    },
    onQuantityInput(item) {
      const originalQty = Number(item.originalQuantity);
      let qty = Number(item.newQuantity);

      if (!Number.isFinite(qty)) qty = originalQty;
      if (qty < 0) qty = 0;
      if (qty > originalQty) qty = originalQty;

      item.newQuantity = qty;
    },
    applyItemReason(item, reason) {
      item.itemReason = reason;
    },
    applyOverallReason(reason) {
      this.overallReason = reason;
    },
    submit() {
      if (!this.isFormValid) {
        Swal.fire('Invalid Input', 'Reduce quantity only (no increase), and provide item and overall reasons for changes.', 'warning');
        return;
      }

      const changedItems = this.adjustments.filter(({ action, newQuantity, originalQuantity }) => {
        if (action === 'REJECT') return true;
        if (action === 'ADJUST_QUANTITY') return Number(newQuantity) !== Number(originalQuantity);
        return false;
      });

      const payload = {
        items: changedItems.map(({ itemId, action, newQuantity, itemReason }) => {
          const normalizedReason = String(itemReason || '').trim();
          const itemPayload = { itemId, action };
          if (action === 'ADJUST_QUANTITY') {
            itemPayload.newQuantity = Number(newQuantity);
          } else if (action === 'REJECT') {
            // Explicit remove-item flow for the API.
            itemPayload.newQuantity = 0;
          }
          itemPayload.itemReason = normalizedReason;
          return itemPayload;
        }),
        reason: String(this.overallReason || '').trim()
      };

      this.$emit('adjust', payload);
    }
  }
}
</script>

<style lang="scss" scoped>
/* Modal Backdrop */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
}

/* Modal Container */
.modal {
  background-color: var(--card-bg);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 640px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}

/* Modal Header */
.modal-header {
  position: relative;
  flex-shrink: 0;
  padding: 24px;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  border-radius: 20px 20px 0 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 25%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 25%);
    opacity: 0.6;
  }

  .gradient-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;

  .header-icon {
    font-size: 2rem;
  }

  h5 {
    margin: 0 0 4px 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .order-number-text {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }
}

.btn-close {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
}

/* Modal Body */
.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-electric-blue-rgb), 0.3);
    border-radius: 3px;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(var(--accent-electric-blue-rgb), 0.2);
    border-top-color: var(--accent-electric-blue);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    margin: 0;
  }
}

/* Adjust Form */
.adjust-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.adjust-item {
  background: var(--secondary-button-bg);
  border-radius: 14px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(var(--accent-electric-blue-rgb), 0.2);
  }
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .item-name {
    font-weight: 600;
    font-size: 1.05rem;
    color: var(--text-primary);
  }

  .item-qty-badge {
    padding: 6px 12px;
    background: var(--card-bg);
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--accent-electric-blue);
    border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  }
}

.item-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.item-reason {
  grid-column: 1 / -1;
}

/* Form Controls */
.form-control, .form-select {
  width: 100%;
  padding: 10px 14px;
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--card-bg);
  border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  border-radius: 10px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-electric-blue);
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb), 0.1);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
}

.form-group {
  .form-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-primary);
    margin-bottom: 12px;

    .label-icon {
      color: var(--accent-electric-blue);
    }
  }
}

/* Predefined Reasons */
.predefined-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.btn-reason {
  padding: 6px 14px;
  background: var(--card-bg);
  border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  color: var(--text-secondary);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
    color: white;
    border-color: var(--accent-electric-blue);
    transform: translateY(-2px);
  }
}

/* Divider */
.divider {
  border: none;
  height: 1px;
  background: rgba(var(--accent-electric-blue-rgb), 0.1);
  margin: 8px 0;
}

/* Modal Footer */
.modal-footer {
  flex-shrink: 0;
  padding: 20px 24px;
  border-top: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    flex-direction: column;
  }
}

.btn-secondary, .btn-primary {
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: var(--secondary-button-bg);
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background: var(--secondary-button-bg-dark);
  }
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(var(--accent-electric-blue-rgb), 0.4);
  }
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;

  .modal {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;

  .modal {
    transform: translateY(40px) scale(0.95);
  }
}
</style>
